import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Check, ChevronRight, Clock, Loader2, Mail, MessageCircle, Pencil, Send, X } from 'lucide-react';
import { FacebookIcon, GoogleBusinessIcon, InstagramIcon, LinkedInIcon } from '../PlatformIcon';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import CreateGbpPostModal, { type GbpPostCollectDraft } from '../CreateGbpPostModal';
import CreateSocialPostModal, {
  type SocialPostCollectDraft,
} from '../CreateSocialPostModal';
import { publishSocialTwin } from '../../lib/socialCrossPost';

// ─────────────────────────────────────────────────────────────────────────────
// Multi Media Campaign wizard: one campaign, many channels. The user sets up
// each channel in order (email → SMS → Google → Facebook → Instagram → LinkedIn) using the
// normal builders in "collect" mode; each finished channel becomes an editable
// block. Nothing is sent/staged until the final Create step, which publishes
// everything now or stages everything for the shared scheduled date.
// Built deliberately as data-in / data-out steps so AI can drive it later.
// ─────────────────────────────────────────────────────────────────────────────

export type MultiMediaChannel = 'email' | 'sms' | 'gbp' | 'facebook' | 'instagram' | 'linkedin';

export const MULTI_MEDIA_CHANNEL_ORDER: MultiMediaChannel[] = [
  'email',
  'sms',
  'gbp',
  'facebook',
  'instagram',
  'linkedin',
];

export interface MultiMediaSchedule {
  sendOption: 'now' | 'schedule';
  scheduledDate: string; // YYYY-MM-DD
  scheduledTime: string; // HH:mm
  timezone: string;
}

export interface RenderCampaignStepArgs {
  channel: 'email' | 'sms';
  /** Previously collected campaignData when re-editing, else null. */
  draft: Record<string, unknown> | null;
  /** Store the built campaignData and advance the wizard. */
  onCollect: (campaignData: Record<string, unknown>) => void;
  /** Back to the wizard overview without saving. */
  onCancel: () => void;
}

interface MultiMediaCampaignWizardProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  channels: MultiMediaChannel[];
  schedule: MultiMediaSchedule;
  /** Page-level email/SMS save handler (handles billing + launch/schedule). */
  onSaveCampaign: (campaignData: Record<string, unknown>) => Promise<void>;
  /** Render the email/SMS builder form (avoids a circular import with Campaigns). */
  renderCampaignStep: (args: RenderCampaignStepArgs) => React.ReactNode;
  onFinished?: () => void;
}

type ChannelDraft =
  | { kind: 'campaign'; data: Record<string, unknown> }
  | { kind: 'gbp'; data: GbpPostCollectDraft }
  | { kind: 'social'; data: SocialPostCollectDraft };

type CreateStatus = 'pending' | 'creating' | 'done' | 'error';

const CHANNEL_META: Record<
  MultiMediaChannel,
  { label: string; Icon: typeof Mail | typeof GoogleBusinessIcon; accent: string; imageIcon?: boolean }
> = {
  email: { label: 'Email', Icon: Mail, accent: 'bg-blue-100 text-blue-700' },
  sms: { label: 'SMS', Icon: MessageCircle, accent: 'bg-green-100 text-green-700' },
  gbp: { label: 'Google Business', Icon: GoogleBusinessIcon, accent: 'bg-white border border-gray-200', imageIcon: true },
  facebook: { label: 'Facebook', Icon: FacebookIcon, accent: 'bg-white border border-gray-200', imageIcon: true },
  instagram: { label: 'Instagram', Icon: InstagramIcon, accent: 'bg-white border border-gray-200', imageIcon: true },
  linkedin: { label: 'LinkedIn', Icon: LinkedInIcon, accent: 'bg-white border border-gray-200', imageIcon: true },
};

function draftSummary(channel: MultiMediaChannel, draft: ChannelDraft): string {
  if (draft.kind === 'campaign') {
    const d = draft.data as Record<string, any>;
    if (channel === 'email') {
      const recipients = d.recipient_count ? ` · ${d.recipient_count} recipients` : '';
      return `${d.subject || d.name || 'Email campaign'}${recipients}`;
    }
    const recipients = d.recipient_count ? ` · ${d.recipient_count} recipients` : '';
    return `${String(d.sms_content || 'SMS campaign').slice(0, 80)}${recipients}`;
  }
  if (draft.kind === 'gbp') {
    return draft.data.content.slice(0, 90);
  }
  const photos = draft.data.mediaIds.length;
  return `${draft.data.content.slice(0, 70)}${photos ? ` · ${photos} photo${photos !== 1 ? 's' : ''}` : ''}`;
}

function draftThumb(draft: ChannelDraft): string | null {
  if (draft.kind === 'gbp') return draft.data.media?.url || null;
  if (draft.kind === 'social') return draft.data.slides[0]?.media.url || null;
  return null;
}

export default function MultiMediaCampaignWizard({
  open,
  onClose,
  businessId,
  channels,
  schedule,
  onSaveCampaign,
  renderCampaignStep,
  onFinished,
}: MultiMediaCampaignWizardProps) {
  const orderedChannels = useMemo(
    () => MULTI_MEDIA_CHANNEL_ORDER.filter((c) => channels.includes(c)),
    [channels],
  );

  const [drafts, setDrafts] = useState<Partial<Record<MultiMediaChannel, ChannelDraft>>>({});
  const [editing, setEditing] = useState<MultiMediaChannel | null>(null);
  const [creating, setCreating] = useState(false);
  const [createStatus, setCreateStatus] = useState<
    Partial<Record<MultiMediaChannel, CreateStatus>>
  >({});

  const firstIncomplete = orderedChannels.find((c) => !drafts[c]) ?? null;
  const allComplete = orderedChannels.every((c) => !!drafts[c]);

  // Auto-open the first channel step when the wizard opens.
  useEffect(() => {
    if (open && editing === null && Object.keys(drafts).length === 0 && firstIncomplete) {
      setEditing(firstIncomplete);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scheduledForIso = useMemo(() => {
    if (schedule.sendOption !== 'schedule' || !schedule.scheduledDate || !schedule.scheduledTime) {
      return null;
    }
    return new Date(`${schedule.scheduledDate}T${schedule.scheduledTime}:00`).toISOString();
  }, [schedule]);

  const scheduleLabel =
    schedule.sendOption === 'now'
      ? 'Posts to every channel immediately'
      : `Everything goes out ${
          schedule.scheduledDate && schedule.scheduledTime
            ? new Date(`${schedule.scheduledDate}T${schedule.scheduledTime}`).toLocaleString(
                undefined,
                { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
              )
            : 'at the scheduled time'
        }`;

  const storeDraft = (channel: MultiMediaChannel, draft: ChannelDraft) => {
    setDrafts((prev) => {
      const next = { ...prev, [channel]: draft };
      const nextIncomplete = orderedChannels.find((c) => !next[c]) ?? null;
      setEditing(nextIncomplete);
      return next;
    });
  };

  const requestClose = () => {
    if (creating) return;
    if (Object.keys(drafts).length > 0 && !allCreated()) {
      const ok = window.confirm(
        'Close the multi media campaign? Your channel setups will be discarded.',
      );
      if (!ok) return;
    }
    setDrafts({});
    setEditing(null);
    setCreateStatus({});
    onClose();
  };

  const allCreated = () =>
    orderedChannels.length > 0 && orderedChannels.every((c) => createStatus[c] === 'done');

  const createGbp = async (draft: GbpPostCollectDraft) => {
    if (scheduledForIso) {
      const { data, error } = await supabase.functions.invoke('gbp-auto-post-action', {
        body: {
          action: 'stage_manual',
          businessId,
          connectionId: draft.connectionId,
          content: draft.content,
          ctaType: draft.ctaType || null,
          ctaUrl: draft.ctaUrl || null,
          scheduledFor: scheduledForIso,
          mediaId: draft.media?.id || null,
        },
      });
      if (error || data?.error) {
        throw new Error(
          data?.error || (await invokeErrorMessage(error, data)) || 'Failed to stage Google post',
        );
      }
      return;
    }
    const { data, error } = await supabase.functions.invoke('ayrshare-gbp-create-post', {
      body: {
        businessId,
        connectionId: draft.connectionId,
        content: draft.content,
        ctaType: draft.ctaType || undefined,
        ctaUrl: draft.ctaUrl || undefined,
        mediaUrl: draft.media?.url || undefined,
        mediaId: draft.media?.id || undefined,
      },
    });
    if (error || data?.error) {
      throw new Error(
        data?.error || (await invokeErrorMessage(error, data)) || 'Failed to publish Google post',
      );
    }
  };

  const createSocial = async (draft: SocialPostCollectDraft) => {
    const mediaIds = draft.mediaIds.length ? draft.mediaIds : null;
    const mediaUrls = draft.mediaUrls.length ? draft.mediaUrls : null;
    if (scheduledForIso) {
      const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
        body: {
          action: 'stage_manual',
          businessId,
          connectionId: draft.connectionId,
          platform: draft.platform,
          content: draft.content,
          mediaIds,
          mediaId: draft.mediaIds[0] || null,
          googleReviewIds: draft.googleReviewIds,
          scheduledFor: scheduledForIso,
          ...(draft.sourceLibraryMediaIds.length
            ? { sourceLibraryMediaIds: draft.sourceLibraryMediaIds }
            : {}),
          ...(draft.instagramOptionsPayload
            ? { instagramOptions: draft.instagramOptionsPayload }
            : {}),
          ...(draft.postFormat && draft.postFormat !== 'feed' ? { postFormat: draft.postFormat } : {}),
          ...(draft.thumbnailUrl ? { thumbnailUrl: draft.thumbnailUrl } : {}),
        },
      });
      if (error || data?.error) {
        throw new Error(
          data?.error ||
            (await invokeErrorMessage(error, data)) ||
            `Failed to stage ${CHANNEL_META[draft.platform].label} post`,
        );
      }
    } else {
    const functionName =
      draft.platform === 'facebook'
        ? 'ayrshare-facebook-posts'
        : draft.platform === 'linkedin'
          ? 'ayrshare-linkedin-posts'
          : 'ayrshare-instagram-posts';
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: {
        action: 'create',
        businessId,
        connectionId: draft.connectionId,
        content: draft.content,
        mediaUrls,
        mediaIds,
        mediaUrl: draft.mediaUrls[0] || null,
        mediaId: draft.mediaIds[0] || null,
        googleReviewIds: draft.googleReviewIds,
        ...(draft.sourceLibraryMediaIds.length
          ? { sourceLibraryMediaIds: draft.sourceLibraryMediaIds }
          : {}),
        ...(draft.instagramOptionsPayload
          ? { instagramOptions: draft.instagramOptionsPayload }
          : {}),
        ...(draft.postFormat && draft.postFormat !== 'feed' ? { postFormat: draft.postFormat } : {}),
        ...(draft.thumbnailUrl ? { thumbnailUrl: draft.thumbnailUrl } : {}),
      },
    });
    if (error || data?.error) {
      throw new Error(
        data?.error ||
          (await invokeErrorMessage(error, data)) ||
          `Failed to publish ${CHANNEL_META[draft.platform].label} post`,
      );
    }
    }
    if (!draft.crossPost || orderedChannels.includes(draft.crossPost.platform)) return;
    const twin = await publishSocialTwin({
      mode: scheduledForIso ? 'schedule' : 'now',
      businessId,
      connectionId: draft.crossPost.connectionId,
      platform: draft.crossPost.platform,
      content: draft.content,
      mediaIds: mediaIds,
      mediaUrls,
      googleReviewIds: draft.googleReviewIds,
      sourceLibraryMediaIds: draft.sourceLibraryMediaIds,
      scheduledFor: scheduledForIso,
      postFormat: draft.postFormat,
      thumbnailUrl: draft.thumbnailUrl,
    });
    if (!twin.ok) {
      const label = draft.crossPost.platform === 'facebook' ? 'Facebook' : 'Instagram';
      toast.error(`${label} did not post: ${twin.message}`);
    }
  };

  const handleCreateAll = async () => {
    if (!allComplete || creating) return;
    setCreating(true);

    // Social/GBP first (fire-and-forget server calls), then SMS, then email last —
    // email may open the page-level billing modal, which should appear after the
    // wizard closes.
    const createOrder: MultiMediaChannel[] = [
      ...orderedChannels.filter(
        (c) => c === 'gbp' || c === 'facebook' || c === 'instagram' || c === 'linkedin',
      ),
      ...orderedChannels.filter((c) => c === 'sms'),
      ...orderedChannels.filter((c) => c === 'email'),
    ];

    let failed = false;
    for (const channel of createOrder) {
      if (createStatus[channel] === 'done') continue;
      const draft = drafts[channel];
      if (!draft) continue;
      setCreateStatus((prev) => ({ ...prev, [channel]: 'creating' }));
      try {
        if (draft.kind === 'gbp') {
          await createGbp(draft.data);
        } else if (draft.kind === 'social') {
          await createSocial(draft.data);
        } else {
          await onSaveCampaign(draft.data);
        }
        setCreateStatus((prev) => ({ ...prev, [channel]: 'done' }));
      } catch (err) {
        console.error(`[MultiMediaCampaign] ${channel} failed:`, err);
        setCreateStatus((prev) => ({ ...prev, [channel]: 'error' }));
        toast.error(
          `${CHANNEL_META[channel].label}: ${err instanceof Error ? err.message : 'failed'}`,
        );
        failed = true;
        break;
      }
    }

    setCreating(false);
    if (!failed) {
      toast.success(
        schedule.sendOption === 'now'
          ? 'Multi media campaign is live'
          : 'Multi media campaign staged for the scheduled time',
      );
      onFinished?.();
      setDrafts({});
      setEditing(null);
      setCreateStatus({});
      onClose();
    }
  };

  if (!open || typeof document === 'undefined') return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const editingCampaignChannel = editing === 'email' || editing === 'sms' ? editing : null;
  const stepNumber = editing ? orderedChannels.indexOf(editing) + 1 : 0;
  const anyErrors = orderedChannels.some((c) => createStatus[c] === 'error');

  const overview = (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3 flex items-center gap-3">
        {schedule.sendOption === 'now' ? (
          <Send className="h-4 w-4 text-violet-700 shrink-0" />
        ) : (
          <Clock className="h-4 w-4 text-violet-700 shrink-0" />
        )}
        <p className="text-[13px] font-medium text-violet-900">{scheduleLabel}</p>
      </div>

      <div className="space-y-2.5">
        {orderedChannels.map((channel, index) => {
          const meta = CHANNEL_META[channel];
          const Icon = meta.Icon;
          const draft = drafts[channel];
          const status = createStatus[channel] || 'pending';
          const isNext = channel === firstIncomplete;
          const thumb = draft ? draftThumb(draft) : null;
          return (
            <button
              key={channel}
              type="button"
              disabled={creating || status === 'done'}
              onClick={() => setEditing(channel)}
              className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3.5 text-left transition-all ${
                status === 'done'
                  ? 'border-emerald-200 bg-emerald-50/60'
                  : status === 'error'
                    ? 'border-red-300 bg-red-50/60'
                    : draft
                      ? 'border-violet-300 bg-white hover:border-violet-400'
                      : isNext
                        ? 'border-violet-500 bg-violet-50/50 hover:bg-violet-50'
                        : 'border-gray-200 bg-gray-50/60'
              } ${creating || status === 'done' ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden ${meta.accent}`}
              >
                <Icon className={meta.imageIcon ? 'h-10 w-10' : 'h-5 w-5'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold text-gray-900">{meta.label}</span>
                  <span className="text-[11px] text-gray-400 font-medium">
                    Step {index + 1} of {orderedChannels.length}
                  </span>
                </div>
                <p className="text-[12px] text-gray-500 truncate mt-0.5">
                  {status === 'done'
                    ? schedule.sendOption === 'now'
                      ? 'Posted'
                      : 'Staged'
                    : status === 'error'
                      ? 'Failed — tap Create again to retry'
                      : draft
                        ? draftSummary(channel, draft)
                        : isNext
                          ? 'Tap to set up'
                          : 'Waiting'}
                </p>
              </div>
              {thumb ? (
                <img
                  src={thumb}
                  alt=""
                  className="h-10 w-10 rounded-lg object-cover border border-gray-200 shrink-0"
                />
              ) : null}
              {status === 'creating' ? (
                <Loader2 className="h-4 w-4 text-violet-600 animate-spin shrink-0" />
              ) : status === 'done' ? (
                <span className="h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                  <Check className="h-3.5 w-3.5" />
                </span>
              ) : draft ? (
                <Pencil className="h-4 w-4 text-violet-500 shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      <p className="text-[12px] text-gray-500">
        Set up each channel in order. Tap a finished block to edit it before creating the
        campaign.
      </p>
    </div>
  );

  const modal = (
    <div
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{ zIndex: 100010, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={requestClose}
    >
      <div
        className={`bg-white shadow-2xl flex flex-col overflow-hidden ${
          isMobile ? 'fixed inset-0 w-full' : 'fixed inset-y-0 right-0 w-full md:w-[800px] lg:w-[900px]'
        }`}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(!isMobile && {
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            {editingCampaignChannel ? (
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-gray-500 hover:text-gray-700 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Back to campaign overview"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            ) : null}
            <h2
              className="text-base sm:text-lg font-semibold text-gray-900 truncate"
              style={{ letterSpacing: '-0.02em' }}
            >
              {editingCampaignChannel
                ? `${CHANNEL_META[editingCampaignChannel].label} · Step ${stepNumber} of ${orderedChannels.length}`
                : 'Multi Media Campaign'}
            </h2>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={creating}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation disabled:opacity-50"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-white" style={{ WebkitOverflowScrolling: 'touch' }}>
          {editingCampaignChannel ? (
            <div className="p-4 sm:p-6">
              {renderCampaignStep({
                channel: editingCampaignChannel,
                draft:
                  drafts[editingCampaignChannel]?.kind === 'campaign'
                    ? (drafts[editingCampaignChannel] as { kind: 'campaign'; data: Record<string, unknown> }).data
                    : null,
                onCollect: (campaignData) =>
                  storeDraft(editingCampaignChannel, { kind: 'campaign', data: campaignData }),
                onCancel: () => setEditing(null),
              })}
            </div>
          ) : (
            overview
          )}
        </div>

        {/* Footer — only on the overview */}
        {!editingCampaignChannel ? (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-white shrink-0 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={requestClose}
              disabled={creating}
              className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleCreateAll()}
              disabled={!allComplete || creating}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : anyErrors ? (
                'Retry failed channels'
              ) : schedule.sendOption === 'now' ? (
                <>
                  <Send className="h-4 w-4" />
                  Create & Post Now
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4" />
                  Create & Schedule
                </>
              )}
            </button>
          </div>
        ) : null}
      </div>

      {/* GBP / social steps render as their own side sheets above the wizard */}
      <CreateGbpPostModal
        open={editing === 'gbp'}
        onClose={() => setEditing(null)}
        connectionId={
          drafts.gbp?.kind === 'gbp' ? drafts.gbp.data.connectionId : null
        }
        subtitle={`Step ${orderedChannels.indexOf('gbp') + 1} of ${orderedChannels.length} · Multi Media Campaign`}
        collect={{
          initialDraft: drafts.gbp?.kind === 'gbp' ? drafts.gbp.data : null,
          onCollect: (draft) => storeDraft('gbp', { kind: 'gbp', data: draft }),
        }}
      />
      <CreateSocialPostModal
        open={editing === 'facebook'}
        onClose={() => setEditing(null)}
        platform="facebook"
        hideCrossPost={channels.includes('facebook') && channels.includes('instagram')}
        connectionId={
          drafts.facebook?.kind === 'social' ? drafts.facebook.data.connectionId : null
        }
        subtitle={`Step ${orderedChannels.indexOf('facebook') + 1} of ${orderedChannels.length} · Multi Media Campaign`}
        collect={{
          initialDraft: drafts.facebook?.kind === 'social' ? drafts.facebook.data : null,
          onCollect: (draft) => storeDraft('facebook', { kind: 'social', data: draft }),
        }}
      />
      <CreateSocialPostModal
        open={editing === 'instagram'}
        onClose={() => setEditing(null)}
        platform="instagram"
        hideCrossPost={channels.includes('facebook') && channels.includes('instagram')}
        connectionId={
          drafts.instagram?.kind === 'social' ? drafts.instagram.data.connectionId : null
        }
        subtitle={`Step ${orderedChannels.indexOf('instagram') + 1} of ${orderedChannels.length} · Multi Media Campaign`}
        collect={{
          initialDraft: drafts.instagram?.kind === 'social' ? drafts.instagram.data : null,
          onCollect: (draft) => storeDraft('instagram', { kind: 'social', data: draft }),
        }}
      />
      <CreateSocialPostModal
        open={editing === 'linkedin'}
        onClose={() => setEditing(null)}
        platform="linkedin"
        hideCrossPost
        connectionId={
          drafts.linkedin?.kind === 'social' ? drafts.linkedin.data.connectionId : null
        }
        subtitle={`Step ${orderedChannels.indexOf('linkedin') + 1} of ${orderedChannels.length} · Multi Media Campaign`}
        collect={{
          initialDraft: drafts.linkedin?.kind === 'social' ? drafts.linkedin.data : null,
          onCollect: (draft) => storeDraft('linkedin', { kind: 'social', data: draft }),
        }}
      />
    </div>
  );

  return createPortal(modal, document.body);
}
